import {
  isSetupResetPending,
  isSetupStepId,
  SETUP_FLOW_VERSION,
  type SetupStepId,
} from '@shared/setup'

/**
 * What setup remembers per user per device (localStorage). Permissions live
 * in the OS and the call-recording answer on the account; this only records
 * how far setup got here.
 */
export type LocalSetupState = {
  /** Flow version of the last first-run completion on this device. */
  flowVersion: number
  /** When first-run setup was finished here (device clock; reporting only). */
  completedAt: string | null
  /** The admin `setupResetAt` this device last honoured (server time). */
  honouredResetAt: string | null
  /** The admin `permissionsResetAt` this device last walked (server time). */
  honouredPermissionsResetAt: string | null
  /** Optional steps already asked once ("Not now" is final). */
  optionalAsked: SetupStepId[]
  /** "Not now" / "Later" count per required step (see `remindsWithBanner`). */
  dismissals: Partial<Record<SetupStepId, number>>
  /** The admin consent request (`consentRequestedAt`, server time) already shown here as a popup. */
  consentRequestShownFor: string | null
  /** Per-step admin requests (`setupRequests["step.<id>"]`) already shown here as a popup. */
  stepRequestsShownFor: Partial<Record<SetupStepId, string>>
}

const KEY_PREFIX = 'ascend-setup:v1:'

export const EMPTY_LOCAL_SETUP_STATE: LocalSetupState = {
  flowVersion: 0,
  completedAt: null,
  honouredResetAt: null,
  honouredPermissionsResetAt: null,
  optionalAsked: [],
  dismissals: {},
  consentRequestShownFor: null,
  stepRequestsShownFor: {},
}

function key(userId: string): string {
  return KEY_PREFIX + userId
}

export function readLocalSetupState(userId: string | null | undefined): LocalSetupState {
  if (!userId) return EMPTY_LOCAL_SETUP_STATE
  try {
    const raw = localStorage.getItem(key(userId))
    if (!raw) return EMPTY_LOCAL_SETUP_STATE
    const v = JSON.parse(raw) as Partial<LocalSetupState>
    const dismissals: LocalSetupState['dismissals'] = {}
    if (v.dismissals && typeof v.dismissals === 'object') {
      for (const [step, count] of Object.entries(v.dismissals)) {
        if (isSetupStepId(step) && typeof count === 'number' && count > 0) dismissals[step] = count
      }
    }
    const stepRequestsShownFor: LocalSetupState['stepRequestsShownFor'] = {}
    if (v.stepRequestsShownFor && typeof v.stepRequestsShownFor === 'object') {
      for (const [step, at] of Object.entries(v.stepRequestsShownFor)) {
        if (isSetupStepId(step) && typeof at === 'string') stepRequestsShownFor[step] = at
      }
    }
    return {
      flowVersion: typeof v.flowVersion === 'number' ? v.flowVersion : 0,
      completedAt: typeof v.completedAt === 'string' ? v.completedAt : null,
      honouredResetAt: typeof v.honouredResetAt === 'string' ? v.honouredResetAt : null,
      honouredPermissionsResetAt:
        typeof v.honouredPermissionsResetAt === 'string' ? v.honouredPermissionsResetAt : null,
      optionalAsked: Array.isArray(v.optionalAsked) ? v.optionalAsked.filter(isSetupStepId) : [],
      dismissals,
      consentRequestShownFor: typeof v.consentRequestShownFor === 'string' ? v.consentRequestShownFor : null,
      stepRequestsShownFor,
    }
  } catch {
    return EMPTY_LOCAL_SETUP_STATE
  }
}

function write(userId: string, next: LocalSetupState): LocalSetupState {
  try {
    localStorage.setItem(key(userId), JSON.stringify(next))
  } catch {
    /* storage unavailable: setup simply shows again next time */
  }
  return next
}

function update(userId: string, fn: (prev: LocalSetupState) => LocalSetupState): LocalSetupState {
  return write(userId, fn(readLocalSetupState(userId)))
}

/**
 * First-run setup is owed on this device: never finished here, finished an
 * older flow, or an admin reset this device hasn't honoured.
 */
export function isFirstRunOwed(
  local: LocalSetupState,
  user: { setupRequests?: unknown } | null | undefined,
): boolean {
  if (!local.completedAt || local.flowVersion < SETUP_FLOW_VERSION) return true
  return isSetupResetPending(local.honouredResetAt, user)
}

/** A reset run asks optional steps and explainers again. */
export function isResetRun(
  local: LocalSetupState,
  user: { setupRequests?: unknown } | null | undefined,
): boolean {
  return Boolean(local.completedAt) && isSetupResetPending(local.honouredResetAt, user)
}

export function markFirstRunComplete(
  userId: string,
  opts: { nowIso: string; honouredResetAt: string | null },
): LocalSetupState {
  return update(userId, (prev) => ({
    ...prev,
    flowVersion: SETUP_FLOW_VERSION,
    completedAt: opts.nowIso,
    honouredResetAt: opts.honouredResetAt ?? prev.honouredResetAt,
  }))
}

/** Starting a reset run: optional steps are asked again and dismissal counts restart. */
export function clearAskedForResetRun(userId: string): LocalSetupState {
  return update(userId, (prev) => ({ ...prev, optionalAsked: [], dismissals: {} }))
}

export function markOptionalAsked(userId: string, step: SetupStepId): LocalSetupState {
  return update(userId, (prev) =>
    prev.optionalAsked.includes(step) ? prev : { ...prev, optionalAsked: [...prev.optionalAsked, step] },
  )
}

export function noteDismissals(userId: string, steps: readonly SetupStepId[]): LocalSetupState {
  return update(userId, (prev) => {
    const dismissals = { ...prev.dismissals }
    for (const step of steps) dismissals[step] = (dismissals[step] ?? 0) + 1
    return { ...prev, dismissals }
  })
}

/** An admin's consent request was put in front of the person once; after that the banner reminds. */
export function markConsentRequestShown(userId: string, requestedAt: string): LocalSetupState {
  return update(userId, (prev) =>
    prev.consentRequestShownFor === requestedAt ? prev : { ...prev, consentRequestShownFor: requestedAt },
  )
}

/** This device finished an admin "Reset device permissions" walk (server stamp). */
export function markPermissionsResetHonoured(userId: string, resetAt: string): LocalSetupState {
  return update(userId, (prev) =>
    prev.honouredPermissionsResetAt === resetAt ? prev : { ...prev, honouredPermissionsResetAt: resetAt },
  )
}

/** These per-step admin requests were put in front of the person (step → request stamp). */
export function markStepRequestsShown(
  userId: string,
  shown: Partial<Record<SetupStepId, string>>,
): LocalSetupState {
  const steps = Object.keys(shown) as SetupStepId[]
  if (steps.length === 0) return readLocalSetupState(userId)
  return update(userId, (prev) => ({
    ...prev,
    stepRequestsShownFor: { ...prev.stepRequestsShownFor, ...shown },
  }))
}

/** A step that got done resets its dismissal count. */
export function clearDismissal(userId: string, step: SetupStepId): LocalSetupState {
  return update(userId, (prev) => {
    if (!prev.dismissals[step]) return prev
    const dismissals = { ...prev.dismissals }
    delete dismissals[step]
    return { ...prev, dismissals }
  })
}

/** Local keys of the prompts setup replaced; cleared once so nothing old resurfaces. */
export const LEGACY_PROMPT_KEYS = [
  'notifications-primer-dismissed',
  'notification-nag-snoozed-until',
  'call-battery-opt-prompted',
  'ascend-welcome-seen',
] as const
export const LEGACY_PROMPT_KEY_PREFIXES = ['ascend-setup-checklist:v1:', 'ascend-recording-consent-offered:'] as const

export function clearLegacyPromptKeys(): void {
  try {
    for (const k of LEGACY_PROMPT_KEYS) localStorage.removeItem(k)
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i)
      if (k && LEGACY_PROMPT_KEY_PREFIXES.some((p) => k.startsWith(p))) doomed.push(k)
    }
    for (const k of doomed) localStorage.removeItem(k)
  } catch {
    /* storage unavailable */
  }
}
